import { MAZE_CELL_SIZE } from '@/constants/maze';
import { gridToMazeCells } from '@/lib/maze/grid-to-maze-cells';
import type { CometSample } from '@/lib/maze/maze-canvas-comet';
import { setMazePixiBackgroundColor } from '@/lib/maze/maze-pixi-background';
import { getMazePixiPalette, type MazeStagePixiPalette } from '@/theme/maze-stage-palette';
import type { DemoMazeLevelDto } from '@/types/api/demo';
import type { GameMazeDto, MoveDirection } from '@/types/api/game';
import type { MazeCell } from '@/types/maze';
import { MazePlayMode } from '@/utils/maze/maze-play-mode';
import * as PIXI from 'pixi.js';
import { BlurFilter } from 'pixi.js';
import { gsap } from 'gsap';
import type { Dispatch, RefObject, SetStateAction } from 'react';
import { createMazeBeaconPixi, createMazePlayerPixi } from './maze-canvas-beacon-player';
import { drawCheckerFloor } from './maze-canvas-floor-draw';
import { PlayerState } from './maze-canvas-types';
import { drawServerGridBoundaryWalls } from './maze-canvas-wall-draw';

export interface BuildMazeCanvasSceneRefs {
  viewportRef: RefObject<PIXI.Container | null>;
  goalCellRef: RefObject<{ x: number; y: number }>;
  mazeColsRef: RefObject<number>;
  mazeRowsRef: RefObject<number>;
  isLiveGameRef: RefObject<boolean>;
  pendingLiveMovesRef: RefObject<{ moveId: string; direction: MoveDirection; movedAt: string }[]>;
  mazeRef: RefObject<MazeCell[][]>;
  positionRef: RefObject<{ x: number; y: number }>;
  playerStateRef: RefObject<PlayerState>;
  trailPointsRef: RefObject<Array<{ x: number; y: number }>>;
  cometSamplesRef: RefObject<CometSample[]>;
  junctionLayerRef: RefObject<PIXI.Container | null>;
  trailRef: RefObject<PIXI.Graphics | null>;
  playerRef: RefObject<PIXI.Container | null>;

  beaconRef: RefObject<PIXI.Container | null>;
  tutorialLayerRef: RefObject<PIXI.Container | null>;
  mazeGridRef: RefObject<number[][]>;
  wallGlowInnerRef: RefObject<PIXI.Graphics | null>;
  wallGlowRef: RefObject<PIXI.Graphics | null>;
}

export interface MazeCanvasSceneSource {
  readonly liveMaze: GameMazeDto | null;
  readonly demoLevel: DemoMazeLevelDto | null;
  readonly previewLevel: DemoMazeLevelDto | null;
}

interface ResolvedMazeSourceForScene {
  mazeGrid: number[][];
  generatedMaze: ReturnType<typeof gridToMazeCells>;
  mazeCols: number;
  mazeRows: number;
  startCol: number;
  startRow: number;
  endCol: number;
  endRow: number;
  isLive: boolean;
}

export function buildMazeCanvasScene(
  app: PIXI.Application,
  playMode: MazePlayMode,
  mazeSourceInput: MazeCanvasSceneSource,
  setMaze: Dispatch<SetStateAction<MazeCell[][]>>,
  refs: BuildMazeCanvasSceneRefs,
  mazePaletteRef: RefObject<MazeStagePixiPalette>,
  showJunctionArrows: (x: number, y: number) => void,
  applyCanvasLayoutRef: RefObject<(() => void) | null>,
): void {
  app.stage.removeChildren();
  switch (playMode) {
    case MazePlayMode.Demo:
    case MazePlayMode.Game:
    case MazePlayMode.Preview:
      break;
    default: {
      const unreachable: never = playMode;
      throw new Error(`Unhandled maze play mode: ${String(unreachable)}`);
    }
  }

  const liveMazeDto = playMode === MazePlayMode.Game ? mazeSourceInput.liveMaze : null;
  const demoMazeDto = playMode === MazePlayMode.Demo ? mazeSourceInput.demoLevel : null;
  const previewMazeDto = playMode === MazePlayMode.Preview ? mazeSourceInput.previewLevel : null;

  const renderFunction = (): ResolvedMazeSourceForScene | null => {
    if (liveMazeDto !== null) {
      return {
        mazeGrid: liveMazeDto.mazeGrid,
        generatedMaze: gridToMazeCells(liveMazeDto.mazeGrid),
        mazeCols: liveMazeDto.cols,
        mazeRows: liveMazeDto.rows,
        startCol: liveMazeDto.currentCol,
        startRow: liveMazeDto.currentRow,
        endCol: liveMazeDto.endCol,
        endRow: liveMazeDto.endRow,
        isLive: true,
      };
    }
    if (demoMazeDto !== null) {
      return {
        mazeGrid: demoMazeDto.mazeGrid,
        generatedMaze: gridToMazeCells(demoMazeDto.mazeGrid),
        mazeCols: demoMazeDto.cols,
        mazeRows: demoMazeDto.rows,
        startCol: demoMazeDto.startCol,
        startRow: demoMazeDto.startRow,
        endCol: demoMazeDto.endCol,
        endRow: demoMazeDto.endRow,
        isLive: false,
      };
    }
    if (previewMazeDto !== null) {
      return {
        mazeGrid: previewMazeDto.mazeGrid,
        generatedMaze: gridToMazeCells(previewMazeDto.mazeGrid),
        mazeCols: previewMazeDto.cols,
        mazeRows: previewMazeDto.rows,
        startCol: previewMazeDto.startCol,
        startRow: previewMazeDto.startRow,
        endCol: previewMazeDto.endCol,
        endRow: previewMazeDto.endRow,
        isLive: false,
      };
    }
    return null;
  };

  const mazeSource = renderFunction();

  if (mazeSource === null) {
    return;
  }

  const { generatedMaze, mazeCols, mazeRows, startCol, startRow } = mazeSource;
  refs.goalCellRef.current = { x: mazeSource.endCol, y: mazeSource.endRow };
  refs.isLiveGameRef.current = mazeSource.isLive;

  refs.mazeColsRef.current = mazeCols;
  refs.mazeRowsRef.current = mazeRows;
  refs.mazeGridRef.current = mazeSource.mazeGrid;
  refs.pendingLiveMovesRef.current = [];

  if (refs.playerRef.current) {
    gsap.killTweensOf(refs.playerRef.current);
  }

  const mazeW = mazeCols * MAZE_CELL_SIZE;
  const mazeH = mazeRows * MAZE_CELL_SIZE;
  app.renderer.resize(mazeW, mazeH);

  setMaze(generatedMaze);
  refs.mazeRef.current = generatedMaze;
  refs.positionRef.current = { x: startCol, y: startRow };
  refs.playerStateRef.current = PlayerState.IDLE;
  refs.trailPointsRef.current = [
    {
      x: startCol * MAZE_CELL_SIZE + MAZE_CELL_SIZE / 2,
      y: startRow * MAZE_CELL_SIZE + MAZE_CELL_SIZE / 2,
    },
  ];
  refs.cometSamplesRef.current = [];

  const palette = getMazePixiPalette();
  mazePaletteRef.current = palette;
  setMazePixiBackgroundColor(app.renderer, palette.floor);

  const viewport = new PIXI.Container();
  const floorLayer = new PIXI.Container();
  const tutorialLayer = new PIXI.Container();
  const trailLayer = new PIXI.Container();
  const wallLayer = new PIXI.Container();
  const junctionLayer = new PIXI.Container();
  const topLayer = new PIXI.Container();
  viewport.addChild(floorLayer, trailLayer, wallLayer, junctionLayer, tutorialLayer, topLayer);
  app.stage.addChild(viewport);
  refs.viewportRef.current = viewport;
  refs.junctionLayerRef.current = junctionLayer;
  refs.tutorialLayerRef.current = tutorialLayer;
  refs.beaconRef.current = null;

  const floor = new PIXI.Graphics();
  drawCheckerFloor(floor, mazeCols, mazeRows, palette);
  floorLayer.addChild(floor);

  const wallGraphics = new PIXI.Graphics();
  wallLayer.addChild(wallGraphics);

  const wallGlowInner = new PIXI.Graphics();
  wallGlowInner.filters = [new BlurFilter(2.5, 3)];
  wallLayer.addChild(wallGlowInner);
  refs.wallGlowInnerRef.current = wallGlowInner;

  const wallGlow = new PIXI.Graphics();
  wallLayer.addChild(wallGlow);
  refs.wallGlowRef.current = wallGlow;
  const mazeWidth = mazeW;
  const mazeHeight = mazeH;

  drawServerGridBoundaryWalls(
    wallGraphics,
    mazeSource.mazeGrid,
    mazeCols,
    mazeRows,
    mazeWidth,
    mazeHeight,
    palette,
  );

  const goal = refs.goalCellRef.current;
  const beacon = createMazeBeaconPixi(goal.x, goal.y, palette);
  topLayer.addChild(beacon);
  refs.beaconRef.current = beacon;

  const trail = new PIXI.Graphics();
  trailLayer.addChild(trail);
  refs.trailRef.current = trail;

  const { player } = createMazePlayerPixi(startCol, startRow, palette);
  topLayer.addChild(player);
  refs.playerRef.current = player;

  showJunctionArrows(startCol, startRow);
  queueMicrotask(() => {
    applyCanvasLayoutRef.current?.();
  });
}
